import { Typography } from '@material-ui/core';
import { Party } from 'api/file/interfaces/get-file-order-insights';
import clsx from 'clsx';
import { pdfProductEnableMessage } from 'features/help/constants';
import { useMemo } from 'react';
import { TransactionStatusEnum } from 'utilities/verification/types';
import EnabledPdfProducts from './EnabledPdfProduct';
import OrderInsightAlert from './OrderInsightAlerts';
import OrderInsightsRiskIcon from './OrderInsightsRiskIcon';
import useStyles from './PdfListStyles';

interface IPdfValidation {
  title: string;
  type?: 'ContactValidation' | 'EnhancedIdentityVerification';
  party: Party;
  hasRisks: boolean;
  isEnabled?: boolean;
  timezone?: string;
}

const PdfValidation = ({ party, title, type = 'ContactValidation', hasRisks, isEnabled, timezone }: IPdfValidation) => {
  const classes = useStyles();

  const hasContactInfo = Boolean(
    party.phoneNumber ||
      party.emailAddress ||
      party?.enhancedIdentifyVerification?.submittedData?.phoneNumber ||
      party?.contactValidation?.submittedData?.phoneNumber ||
      party?.enhancedIdentifyVerification?.submittedData?.emailAddress ||
      party?.contactValidation?.submittedData?.emailAddress,
  );

  const riskIcon = useMemo(() => {
    if (!isEnabled) return 'None';
    if (!hasContactInfo) return 'None';
    if (type === 'EnhancedIdentityVerification') {
      const status = party.enhancedIdentifyVerification?.transactionStatus;

      const isPartyTransactionPending = [
        TransactionStatusEnum.Pending,
        TransactionStatusEnum.AwaitingUserAction,
      ].includes(status ?? ('' as TransactionStatusEnum));

      if (isPartyTransactionPending) return 'Pending';
      if (status === TransactionStatusEnum.Failed) return 'Error';
      if (party?.enhancedIdentifyVerification) {
        return hasRisks ? 'Alert' : 'Success';
      }
      return 'None';
    }
    if (type === 'ContactValidation' && party?.contactValidation) {
      return hasRisks ? 'Alert' : 'Success';
    }
    return 'None';
  }, [hasRisks, party, isEnabled, type, hasContactInfo]);

  return (
    <div className={classes.brdBtm}>
      <div className={clsx(classes.flex, classes.paddingLeftZero, classes.typographyFontSize)}>
        <div className={classes.labelCol}>
          <span className={classes.flexCenter}>
            <OrderInsightsRiskIcon riskSeverity={riskIcon} isPdf />
            <Typography
              variant="h4"
              color="textSecondary"
              className={clsx(classes.marginLeft, classes.typographyFontSize, classes.bold)}
            >
              {title}:
            </Typography>
          </span>
        </div>
        <div className={classes.flexGrow}>
          {isEnabled ? (
            <EnabledPdfProducts type={type} party={party} timezone={timezone} />
          ) : (
            <OrderInsightAlert
              isPdf
              unknowns={[
                {
                  severityName: 'None',
                  category: 0,
                  categoryName: '',
                  type: 0,
                  typeName: '',
                  severity: 0,
                  title: '',
                  description: pdfProductEnableMessage,
                },
              ]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PdfValidation;
